import i18n from "i18next";
import { useId } from "react";
import type { ClassOptionsGridProps, ClassOptionsValue } from "@/types/documentClass";
import { Segmented } from "./Segmented";
import styles from "./ClassOptionsGrid.module.scss";

/**
 * ClassOptionsGrid — six controls in a 2-column grid that mirror the
 * universal LaTeX class options.
 *
 * Per the round-2 design:
 * - Page size, Font size, Sides, Orientation, Title page, Columns are all
 *   segmented controls (Title page is NOT a toggle).
 * - When `columns === 2`, a `Balance at end` checkbox fades in below
 *   (120ms transition). Switching back to 1 hides it without losing the
 *   value.
 *
 * `hidden` is reserved for future class-specific gating (Beamer hides
 * most options); empty in v1.
 */
export function ClassOptionsGrid(props: ClassOptionsGridProps) {
  const { value, onChange, hidden } = props;
  const balanceId = useId();
  const hiddenSet = new Set<keyof ClassOptionsValue>(hidden ?? []);

  function update<K extends keyof ClassOptionsValue>(key: K, next: ClassOptionsValue[K]) {
    onChange({ ...value, [key]: next });
  }

  const showColumnsBalance = value.columns === 2;

  return (
    <div className={styles.grid} data-testid="class-options-grid">
      {!hiddenSet.has("paperSize") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.pageSize")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.pageSize")}
            testId="class-option-page-size"
            value={value.paperSize}
            options={[
              { value: "a4", label: "A4" },
              { value: "letter", label: i18n.t("shell:classOptions.letter") },
              { value: "a5", label: "A5" },
            ]}
            onChange={(next) => update("paperSize", next)}
          />
        </div>
      )}

      {!hiddenSet.has("fontSize") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.fontSize")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.fontSize")}
            testId="class-option-font-size"
            value={value.fontSize}
            options={[
              { value: 10, label: "10pt" },
              { value: 11, label: "11pt" },
              { value: 12, label: "12pt" },
            ]}
            onChange={(next) => update("fontSize", next)}
          />
        </div>
      )}

      {!hiddenSet.has("columns") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.columns")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.columns")}
            testId="class-option-columns"
            value={value.columns}
            options={[
              { value: 1, label: "1" },
              { value: 2, label: "2" },
            ]}
            onChange={(next) => update("columns", next)}
          />
          <div
            className={
              showColumnsBalance
                ? `${styles.balanceWrap} ${styles.balanceWrapVisible}`
                : styles.balanceWrap
            }
            aria-hidden={!showColumnsBalance}
          >
            <label className={styles.balanceLabel} htmlFor={balanceId}>
              <input
                id={balanceId}
                type="checkbox"
                className={styles.balanceCheckbox}
                checked={value.balancedColumns}
                tabIndex={showColumnsBalance ? 0 : -1}
                onChange={(event) => update("balancedColumns", event.target.checked)}
              />
              <span>{i18n.t("shell:classOptions.balanceAtEnd")}</span>
            </label>
            <p className={styles.balanceHelp}>
              {i18n.t("shell:classOptions.usesTheMulticolPackageLast")}
            </p>
          </div>
        </div>
      )}

      {!hiddenSet.has("sides") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.sides")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.sides")}
            testId="class-option-sides"
            value={value.sides}
            options={[
              { value: "one", label: i18n.t("shell:classOptions.1Sided") },
              { value: "two", label: i18n.t("shell:classOptions.2Sided") },
            ]}
            onChange={(next) => update("sides", next)}
          />
        </div>
      )}

      {!hiddenSet.has("orientation") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.orientation")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.orientation")}
            testId="class-option-orientation"
            value={value.orientation}
            options={[
              { value: "portrait", label: i18n.t("shell:classOptions.portrait") },
              { value: "landscape", label: i18n.t("shell:classOptions.landscape") },
            ]}
            onChange={(next) => update("orientation", next)}
          />
        </div>
      )}

      {!hiddenSet.has("titlePage") && (
        <div className={styles.cell}>
          <span className={styles.label}>{i18n.t("shell:classOptions.titlePage")}</span>
          <Segmented
            ariaLabel={i18n.t("shell:classOptions.titlePage")}
            testId="class-option-title-page"
            value={value.titlePage ? "titlepage" : "inline"}
            options={[
              { value: "inline", label: i18n.t("shell:classOptions.inline") },
              { value: "titlepage", label: i18n.t("shell:classOptions.titlePage") },
            ]}
            onChange={(next) => update("titlePage", next === "titlepage")}
          />
        </div>
      )}
    </div>
  );
}
